import React, {useMemo} from 'react';
import clsx from 'clsx';
import {FormLabel, Skeleton} from '@mui/material';
import {isEmptyValue} from '@netx/core-components/Form';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {
	getReadOnlyTextFieldUtilityClass,
	readOnlyTextFieldClasses,
	type ReadOnlyTextFieldClasses,
} from './readOnlyTextFieldClasses';
import type {TextFieldProps} from './TextField';

type ReadOnlyTextFieldOwnerState = WithClasses<
	ReadOnlyTextFieldClasses,
	{
		disabled?: boolean;
		limitHeightToContainer?: boolean;
		pseudoValue?: boolean;
		wrapLabel?: boolean;
	}
>;

const useUtilityClasses = (ownerState: ReadOnlyTextFieldOwnerState) => {
	const {classes, disabled, limitHeightToContainer, pseudoValue, wrapLabel} =
		ownerState;
	const slots = {
		root: [
			'root',
			disabled && 'disabled',
			limitHeightToContainer && 'limitHeightToContainer',
			pseudoValue && 'pseudoValue',
			wrapLabel && 'wrapLabel',
		],
		label: ['label'],
		value: ['value'],
	};
	return composeClasses(slots, getReadOnlyTextFieldUtilityClass, classes);
};

const ReadOnlyTextFieldRoot = styled('div')(({theme}) => ({
	margin: theme.spacing(0.75, 0),
	padding: theme.spacing(0, 1.75),
	width: '100%',
	[`&.${readOnlyTextFieldClasses.disabled}`]: {
		...theme.mixins.disabled,
	},
	[`&.${readOnlyTextFieldClasses.pseudoValue}`]: {
		'& $value': {
			color: theme.palette.text.secondary,
		},
	},
	[`&.${readOnlyTextFieldClasses.limitHeightToContainer}`]: {
		maxHeight: '100%',
		display: 'flex',
		flexDirection: 'column',
		'& $label': {
			flex: '0 0 auto',
		},
		'& $value': {
			...theme.mixins.scrollable,
		},
	},
}));

const ReadOnlyTextFieldValue = styled('div')(({theme}) => ({
	// for \n etc (no need to parse a string and add <br/>s etc)
	whiteSpace: 'pre-wrap',
	width: '100%',
	wordBreak: 'break-word',
}));

const ReadOnlyTextFieldLabel = styled(FormLabel)(({theme}) => ({
	...theme.mixins.readOnlyLabel,
}));

const ReadOnlyTextFieldCodeValue = styled('span')(({theme}) => ({
	background: theme.palette.custom.background.light,
	padding: `2px 3px`,
	borderRadius: 2,
	fontFamily: 'source-code-pro, monospace',
	fontSize: '0.8125rem',
}));

export interface ReadOnlyTextFieldProps
	extends
		Omit<TextFieldProps, 'classes' | 'name' | 'onChange'>,
		WithClasses<ReadOnlyTextFieldClasses> {
	limitHeightToContainer?: boolean;
	multipleValues?: boolean;
	wrapLabel?: boolean;
	value?: string | null;
	isCode?: boolean;
}

export const ReadOnlyTextFieldPrimitive = ({
	classes: _classes,
	className,
	disabled,
	label = '',
	limitHeightToContainer,
	loading,
	loadingValue,
	multipleValues,
	wrapLabel,
	value,
	isCode,
}: ReadOnlyTextFieldProps) => {
	value = value == null ? '' : value;

	const pseudoValue = isEmptyValue(value) || multipleValues;

	const displayValue = useMemo(() => {
		if (loading || loadingValue)
			return <Skeleton variant="text" width={200} height={22} />;
		if (multipleValues) return i18n.t('i18n.multipleValues');
		if (value === '') return '(none)';
		return value;
	}, [loading, loadingValue, multipleValues, value]);

	const ownerState: ReadOnlyTextFieldOwnerState = {
		classes: _classes,
		disabled,
		limitHeightToContainer,
		pseudoValue,
		wrapLabel,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<ReadOnlyTextFieldRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
		>
			{label != null && label !== '' ? (
				loading ? (
					<Skeleton variant="text" width={150} height={22} />
				) : (
					<ReadOnlyTextFieldLabel
						className={classes.label}
						title={typeof label === 'string' ? label : undefined}
					>
						{label}
					</ReadOnlyTextFieldLabel>
				)
			) : null}
			<ReadOnlyTextFieldValue className={classes.value}>
				{isCode ? (
					<ReadOnlyTextFieldCodeValue>
						{displayValue}
					</ReadOnlyTextFieldCodeValue>
				) : (
					displayValue
				)}
			</ReadOnlyTextFieldValue>
		</ReadOnlyTextFieldRoot>
	);
};

export const ReadOnlyTextField = ReadOnlyTextFieldPrimitive;

export default ReadOnlyTextField;
